import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, AlertCircle, Home, ExternalLink } from 'lucide-react';

import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CompactSearchBar } from '../components/CompactSearchBar';
import { PopularDestinationsGrid } from '../components/PopularDestinations';
import { DynamicPageBanner } from '../components/DynamicPageBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Partner Offer Page — public landing page for partner/Stripe offers.
 * Route: /offers/:pageId/:lang? (lang optional)
 *
 * Extracted from App.js (Fase 1) — pure relocation, no logic changes.
 */
export const PartnerOfferPage = () => {
  const { pageId, lang } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use URL language or current i18n language
  const displayLang = lang || i18n.language || 'en';

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/partner-page/${pageId}/${displayLang}`);
        setPageData(response.data);
      } catch (err) {
        console.error('Failed to load partner page:', err);
        setError('Page not found or not available in this language');
      } finally {
        setLoading(false);
      }
    };

    if (pageId) {
      loadPage();
    }
  }, [pageId, displayLang]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-auto text-center p-8">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate('/')}>
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </Card>
      </div>
    );
  }

  const content = pageData.content;

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Compact Search Bar */}
      <div>
        <CompactSearchBar />
      </div>

      {/* Page Banner */}
      <DynamicPageBanner page="partner-offers" />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-[1760px]">
        {/* Banner Image */}
        {content.banner_image && (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-xl">
            <img
              src={content.banner_image}
              alt={content.title}
              className="w-full h-auto"
            />
          </div>
        )}

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-6">
          {content.title}
        </h1>

        {/* Pricing */}
        {(content.price || content.original_price) && (
          <div className="flex items-center justify-center gap-4 mb-8">
            {content.original_price && (
              <span className="text-2xl text-muted-foreground line-through">
                {content.original_price}
              </span>
            )}
            {content.price && (
              <span className="text-4xl font-bold text-green-600">
                {content.price}
              </span>
            )}
          </div>
        )}

        {/* Description (HTML) */}
        {content.description && (
          <Card className="mb-8">
            <CardContent className="p-6 md:p-8 prose prose-lg max-w-none">
              <div
                dangerouslySetInnerHTML={{ __html: content.description }}
                className="[&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-4 [&>li]:mb-2 [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mb-4 [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mb-3 [&>img]:max-w-full [&>img]:rounded-lg [&>img]:my-4"
              />
            </CardContent>
          </Card>
        )}

        {/* CTA Button */}
        {content.stripe_link && (
          <div className="text-center">
            <a
              href={content.stripe_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold text-xl px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              {content.cta_text || 'Get Your Pass Now'}
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        )}

        {/* Popular Destinations */}
        <PopularDestinationsGrid limit={8} />

        {/* Footer */}
        <div className="mt-12 pt-8 border-t text-center text-muted-foreground">
          <p className="text-sm">
            © {new Date().getFullYear()} TravelAR Group BV. All rights reserved.
          </p>
          <div className="mt-4">
            <Button variant="link" onClick={() => navigate('/')}>
              Back to Freestays
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerOfferPage;
